import { devAssert, devAssertNonNullable } from '@/devtools/dev-assert';
import type { TextData } from '@/teoCore/TeoStorage';

import type {
  AssistantInstruction,
  EditHistoryParagraph,
  EditHistoryStep,
  HistoryIndexScore,
} from '../../TeoApi';
import type { TeoText } from '../TeoText';
import type { AutoSuggestionState } from './AutoSuggestionHandler';
import type { QuickRequest } from './quickSuggestionRequest';

const ORIGINAL_HISTORY_ID = 'original';
const MANUAL_EDIT_TITLE = 'Manuell bearbeitet';
const FULL_TEXT_PARAGRAPH_ID = 'full_text';

export interface AutoAiHistoryEntry extends EditHistoryStep {
  textData: TextData;
  id: string;
  /** Absent on the session original. Auto applies use `auto`. */
  source?: 'auto' | 'manual';
}

function isOriginalEntry(entry: Pick<AutoAiHistoryEntry, 'id'>): boolean {
  return entry.id === ORIGINAL_HISTORY_ID;
}

function isManualEntry(entry: Pick<AutoAiHistoryEntry, 'source'>): boolean {
  return entry.source === 'manual';
}

/** The slice of the auto store this history reads and writes. */
interface AutoHistoryStore {
  readonly state: AutoSuggestionState;
  getHistoryById(id: string): AutoAiHistoryEntry | undefined;
  getLatestHistoryEntry(): AutoAiHistoryEntry | undefined;
  setStateValue(prop: 'uiHistory', value: AutoAiHistoryEntry[]): boolean;
  setStateValue(prop: 'activeHistoryId', value: string | null): boolean;
  update(
    cb: (
      state: AutoSuggestionState,
    ) => Partial<
      Pick<AutoSuggestionState, 'uiHistory' | 'activeHistoryId' | 'skippedInstructions'>
    >,
  ): void;
}

/**
 * The applied steps of one auto session, newest first, with the original text last.
 * Previewing, restoring, and recording steps all go through here. The suggestion loop
 * decides when a step is recorded.
 */
export class AutoSuggestionHistory {
  #nextStepNr = 0;

  constructor(
    private text: TeoText,
    private store: AutoHistoryStore,
  ) {}

  hasHistory(): boolean {
    return this.store.state.uiHistory.length > 1;
  }

  selectHistory(item: AutoAiHistoryEntry): void {
    const uiHistory = this.store.state.uiHistory;
    const idx = uiHistory.findIndex((x) => x.id === item.id);
    if (!devAssert(idx !== -1, 'Item not found in history')) return;
    this.viewHistory(item.id);
    this.applyHistoryText(item.textData);
  }

  showHistoryChanges(item: AutoAiHistoryEntry): void {
    const uiHistory = this.store.state.uiHistory;
    const idx = uiHistory.findIndex((x) => x.id === item.id);

    if (!devAssert(idx !== -1, 'Item not found in history')) return;
    // The editor is ahead of this snapshot. Reloading it would drop that edit.
    if (this.isViewing(item.id) && item.textData.text !== this.text.text) {
      const previousText = uiHistory[idx + 1]?.textData.text;
      if (previousText !== undefined) this.showDiffTo(previousText);
      return;
    }
    if (isOriginalEntry(item)) {
      this.viewHistory(item.id);
      this.text.diff.hide();
      this.applyHistoryText(item.textData);
      return;
    }

    const prevHistoryTextData = uiHistory[idx + 1]?.textData;
    if (!devAssertNonNullable(prevHistoryTextData, `Previous history not found @${idx - 1}`))
      return;

    this.viewHistory(item.id);
    this.applyHistoryText(item.textData);
    this.showDiffTo(prevHistoryTextData.text);
  }

  restoreHistory(item: AutoAiHistoryEntry): void {
    const uiHistory = this.store.state.uiHistory;
    const idx = uiHistory.findIndex((entry) => entry.id === item.id);
    if (!devAssert(idx !== -1, 'Item not found in history')) return;

    this.applyHistoryText(item.textData);
    this.text.diff.hide();
    this.store.update(() => ({
      uiHistory: uiHistory.slice(idx),
      skippedInstructions: [],
      activeHistoryId: null,
    }));
  }

  cloneHistory(
    forText?: Pick<TeoText, 'id' | 'ui'>,
    upTo?: Pick<AutoAiHistoryEntry, 'id'>,
  ): AutoAiHistoryEntry[] {
    const upToItem = upTo ? this.store.getHistoryById(upTo.id) : this.store.getLatestHistoryEntry();
    const idx = this.store.state.uiHistory.findIndex((entry) => entry.id === upToItem?.id);
    if (!devAssert(idx !== -1, 'Item not found in history')) return [];

    const source = this.store.state.uiHistory.slice(idx);
    return source.map((entry) => {
      const clonedEntry = {
        ...entry,
        textData: {
          ...entry.textData,
          id: forText?.id ?? entry.textData.id,
        },
      };
      if (forText?.ui.state.title && clonedEntry.textData.uiState) {
        clonedEntry.textData.uiState.title = forText.ui.state.title;
      }
      return clonedEntry;
    });
  }

  cloneHistoryToText(text: TeoText, upTo?: Pick<AutoAiHistoryEntry, 'id'>): void {
    const cloned = this.cloneHistory(text, upTo);
    text.ai.auto.store.reinit({
      ...this.store.state,
      uiHistory: cloned,
      workTextId: null,
    });
    const latestHistoryItem = cloned.at(0);
    if (!latestHistoryItem) return;
    text.ai.auto.history.restoreHistory(latestHistoryItem);
  }

  /**
   * Remembers the current text as the session original when autopilot opens and nothing
   * has been stored yet. A later manual edit is then a step on top of that baseline.
   */
  ensureSessionBaseline(): void {
    if (this.store.state.uiHistory.length === 0) {
      this.store.setStateValue('uiHistory', [this.createOriginalEntry()]);
    }
  }

  /**
   * Attaches the session baseline and applied steps, or leaves them off while nothing has
   * been applied yet.
   */
  withEditHistory(request: QuickRequest): QuickRequest {
    const uiHistory = this.store.state.uiHistory;
    const original = uiHistory.at(-1);
    const steps = uiHistory.filter((entry) => !isOriginalEntry(entry)).toReversed();
    if (steps.length === 0 || !original || !isOriginalEntry(original)) return request;
    return {
      ...request,
      original_text: original.textData.text,
      original_tpi: original.tpi,
      original_indices: original.indices,
      edit_history: steps.map(({ paragraphs, tpi, instruction_title, indices }) => ({
        paragraphs,
        tpi,
        instruction_title,
        indices,
      })),
    };
  }

  /**
   * Adds an auto step on top of the newest entry.
   * @param source The text the step was applied to and analyzed on, which is not the text
   * the user sees while a run is going on.
   */
  recordAppliedStep(
    source: TeoText,
    instruction: AssistantInstruction | undefined,
    paragraphs: EditHistoryParagraph[],
    instructionTitle?: string,
  ): void {
    const tpi = source.analysis?.getTPI();
    if (tpi === undefined) return;
    const previousNewestId = this.store.getLatestHistoryEntry()?.id;
    const step: AutoAiHistoryEntry = {
      id: this.getNextStepId(),
      source: 'auto',
      paragraphs,
      tpi,
      indices: this.indexScores(source),
      textData: source.getTextData(),
    };
    const title = instructionTitle ?? instruction?.short_title;
    if (title) step.instruction_title = title;
    this.store.setStateValue('uiHistory', [step, ...this.store.state.uiHistory]);
    this.keepFollowingNewest(previousNewestId);
  }

  /**
   * Loads the newest entry into the text when the user follows the newest entry, and
   * leaves the text alone while they look at an older one.
   * @param showDiff Marks what changed since the entry before.
   */
  followNewest(showDiff: boolean): void {
    if (this.store.state.activeHistoryId !== null) return;
    const [latest, previous] = this.store.state.uiHistory;
    if (!latest) return;
    this.applyHistoryText(latest.textData);
    if (showDiff && previous) this.showDiffTo(previous.textData.text);
  }

  /**
   * Writes a manual edit when the analysed text is not the newest history entry. A second
   * manual edit updates that entry; an auto step in between starts a new one. The whole
   * text is stored, because a manual edit is not a paragraph rewrite.
   */
  commitManualEdit(): void {
    const latest = this.store.getLatestHistoryEntry();
    const tpi = this.text.analysis?.getTPI();
    if (!latest || tpi === undefined) return;

    const text = this.text.text;
    if (text === latest.textData.text && !isManualEntry(latest)) return;
    const entry: AutoAiHistoryEntry = {
      id: isManualEntry(latest) ? latest.id : this.getNextStepId(),
      source: 'manual',
      paragraphs: [{ id: FULL_TEXT_PARAGRAPH_ID, text }],
      tpi,
      indices: this.indexScores(),
      textData: this.text.getTextData(),
      instruction_title: MANUAL_EDIT_TITLE,
    };
    const rest =
      isManualEntry(latest) ? this.store.state.uiHistory.slice(1) : this.store.state.uiHistory;
    const active = this.store.state.activeHistoryId;
    this.store.update(() => ({
      uiHistory: [entry, ...rest],
      ...(active === null || active === latest.id ? { activeHistoryId: null } : {}),
    }));
  }

  private createOriginalEntry(): AutoAiHistoryEntry {
    return {
      id: ORIGINAL_HISTORY_ID,
      paragraphs: [],
      textData: this.text.getTextData(),
      tpi: this.text.analysis?.getTPI() ?? 0,
      indices: this.indexScores(),
      instruction_title: 'Ursprünglicher Text',
    };
  }

  /** Used index scores from the current analysis of `source`, in profile order. */
  private indexScores(source: TeoText = this.text): HistoryIndexScore[] | undefined {
    const indices = source.analysis?.getIndices();
    if (!indices) return;
    const scores = Object.values(indices)
      .filter((index) => index.used)
      .map(({ title, score }) => ({ title, score }));
    return scores.length > 0 ? scores : undefined;
  }

  private getNextStepId(): string {
    const nr = this.#nextStepNr++;
    return `step-${nr}`;
  }

  /**
   * Loads a snapshot without leaving the assistant mode or view the editor is already in.
   * The session original was stored before autopilot was turned on, so its own state would
   * switch back to manual. The UI state (selected dimension, drawer, markers) also stays the
   * editor's own: snapshots of the work text carry the UI state from when the loop started.
   */
  private applyHistoryText(textData: TextData): void {
    const { performanceMode, view, previousView, selectedPromptOptions, currentTemplateId } =
      this.text.store.state;
    this.text.updateTextData({
      ...textData,
      uiState: { ...this.text.ui.state },
      storeState: {
        selectedPromptOptions,
        currentTemplateId,
        ...textData.storeState,
        performanceMode,
        view,
        previousView,
      },
    });
  }

  /** Marks what changed in the text since `previousText`. */
  private showDiffTo(previousText: string): void {
    this.text.diff.show();
    this.text.diff.update(previousText, true);
  }

  /** Points the editor at a history entry. The newest entry stays `null` so new steps follow it. */
  private viewHistory(id: string): void {
    const newestId = this.store.getLatestHistoryEntry()?.id;
    this.store.setStateValue('activeHistoryId', newestId === id ? null : id);
  }

  private isViewing(id: string): boolean {
    const active = this.store.state.activeHistoryId;
    if (active === null) return id === this.store.getLatestHistoryEntry()?.id;
    return active === id;
  }

  /** New steps stay selected when the editor was already on the previous newest entry. */
  private keepFollowingNewest(previousNewestId: string | undefined): void {
    const active = this.store.state.activeHistoryId;
    if (active === null || active === previousNewestId) {
      this.store.setStateValue('activeHistoryId', null);
    }
  }
}
